import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import MuiRadio from '@mui/material/Radio';
import MuiRadioGroup from '@mui/material/RadioGroup';
import MuiFormControlLabel from '@mui/material/FormControlLabel';
import MuiFormControl from '@mui/material/FormControl';
import MuiFormLabel from '@mui/material/FormLabel';
import type { SxProps, Theme } from '@mui/material/styles';
import type { BackgroundThemes } from 'shared/const';
import theme from 'shared/themes/default';
import T from 'shared/atoms/Typography';

const RadioAndLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

interface RadioOption {
  label: string;
  value: string;
  secondaryLabel?: string;
}

interface RadioGroupProps {
  defaultValue?: string;
  labelText?: string;
  onChange: (value: string) => void;
  options: RadioOption[];
  outlined?: boolean;
  sx?: SxProps<Theme>;
  theme?: BackgroundThemes;
  visibility?: 'high' | 'low';
}

const RadioGroup = ({
  defaultValue,
  labelText,
  onChange,
  options,
  outlined = false,
  sx = {},
  theme: themeLayout = 'dark',
  visibility = 'high',
  ...props
}: RadioGroupProps) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
    }
  }, [defaultValue]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setValue(value);
    onChange(value);
  };

  let selectedColor: string;

  if (themeLayout === 'light') {
    selectedColor = theme.palette.mainDark;
  } else if (visibility === 'high') {
    selectedColor = theme.palette.brand;
  } else {
    selectedColor = theme.palette.lightPure;
  }

  const sxLabel: SxProps<Theme> = {
    color:
      themeLayout === 'dark' ? theme.palette.lightPure : theme.palette.mainDark,
    marginBottom: theme.spacing.s,
  };

  const sxRadioLabel: SxProps<Theme> = {
    marginLeft: outlined ? 0 : `-${theme.spacing.xs}`,
    color:
      themeLayout === 'dark' ? theme.palette.lightPure : theme.palette.mainDark,
  };

  const sxCheckedLabel: SxProps<Theme> = {
    '& .MuiFormControlLabel-label': {
      fontWeight: 600,
      color: selectedColor,
    },
  };

  if (outlined) {
    sxRadioLabel.borderRadius = '6px';
    sxRadioLabel.border = `1px solid ${theme.palette.disabledLight}`;
    sxRadioLabel.marginRight = 0;
    sxRadioLabel.padding = theme.spacing.m1;
    sxRadioLabel.width = '100%';
    sxCheckedLabel.backgroundColor =
      themeLayout === 'dark'
        ? theme.palette.darkWrapper
        : theme.palette.lightDistinct;
  }

  const sxRadio: SxProps<Theme> = {
    color:
      themeLayout === 'dark' ? theme.palette.lightPure : theme.palette.mainDark,
    marginRight: outlined ? theme.spacing.m1 : theme.spacing.xs,
    padding: theme.spacing.xs,
    '&.Mui-checked': {
      color: selectedColor,
    },
    '&:hover, &:focus, &:active': {
      color:
        themeLayout === 'dark' ? theme.palette.brand : theme.palette.mainDark,
    },
  };

  return (
    <MuiFormControl sx={{ width: '100%' }}>
      {labelText && (
        <MuiFormLabel id="radio-buttons-group-label" sx={sxLabel}>
          {labelText}
        </MuiFormLabel>
      )}
      <MuiRadioGroup
        aria-labelledby="radio-buttons-group-label"
        name="radio-buttons-group"
        onChange={handleChange}
        sx={{ ...sx, ...(outlined ? { gap: theme.spacing.s } : {}) }}
        value={value}
        {...props}
      >
        {options.map((option) => (
          <RadioAndLabelWrapper key={option.value}>
            <MuiFormControlLabel
              sx={{
                ...sxRadioLabel,
                ...(option.value === value ? sxCheckedLabel : {}),
              }}
              value={option.value}
              control={<MuiRadio sx={sxRadio} />}
              label={option.label}
            />
            {option.secondaryLabel && (
              <T variant="paragraphS" color={theme.palette.disabledLight}>
                {option.secondaryLabel}
              </T>
            )}
          </RadioAndLabelWrapper>
        ))}
      </MuiRadioGroup>
    </MuiFormControl>
  );
};

export default RadioGroup;
export type { RadioGroupProps, RadioOption };
